import { useMutation } from "@apollo/client";
import { Button } from "@dashboard/components/Button";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import useNotifier from "@dashboard/hooks/useNotifier";
import { buttonMessages } from "@dashboard/intl";
import { Box, Divider, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { googleMerchantFeedPushMutation } from "../../mutations";
import { GoogleMerchantPushResult } from "../../types";
import { messages } from "./messages";

interface GoogleExportModalProps {
  open: boolean;
  channelSlug: string;
  storefrontUrl: string;
  onClose: () => void;
  onPushed?: (feedUrl: string | null) => void;
}

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid #e5e7eb",
  fontSize: 14,
  backgroundColor: "#fff",
  width: "100%",
};

export const GoogleExportModal = ({
  open,
  channelSlug,
  storefrontUrl,
  onClose,
  onPushed,
}: GoogleExportModalProps) => {
  const intl = useIntl();
  const notify = useNotifier();

  const [merchantId, setMerchantId] = useState("");
  const [serviceAccountKey, setServiceAccountKey] = useState("");
  const [confirmState, setConfirmState] = useState<ConfirmButtonTransitionState>("default");
  const [result, setResult] = useState<GoogleMerchantPushResult | null>(null);

  const [push] = useMutation(googleMerchantFeedPushMutation, {
    onCompleted: data => {
      const payload = data?.googleMerchantFeedPush;

      if (payload?.googleMerchantErrors?.length > 0) {
        setConfirmState("error");
        notify({
          status: "error",
          title: intl.formatMessage(messages.toastErrorTitle),
          text: payload.googleMerchantErrors.map((e: { message: string }) => e.message).join(", "),
        });

        return;
      }

      setResult(payload);
      setConfirmState(payload.failed > 0 ? "error" : "success");
      onPushed?.(payload.feedUrl ?? null);
      notify({
        status: payload.failed > 0 ? "warning" : "success",
        title: intl.formatMessage(messages.toastSuccessTitle),
      });
    },
    onError: error => {
      setConfirmState("error");
      notify({
        status: "error",
        title: intl.formatMessage(messages.toastErrorTitle),
        text: error.message,
      });
    },
  });

  const resetState = () => {
    setMerchantId("");
    setServiceAccountKey("");
    setConfirmState("default");
    setResult(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = () => {
    setResult(null);
    setConfirmState("loading");
    notify({ title: intl.formatMessage(messages.toastStartedTitle) });
    push({
      variables: {
        input: {
          channel: channelSlug,
          merchantId,
          serviceAccountKey,
          storefrontUrl: storefrontUrl || undefined,
        },
      },
    });
  };

  const canSubmit = !!channelSlug && !!merchantId.trim() && !!serviceAccountKey.trim();

  return (
    <DashboardModal onChange={handleClose} open={open}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Header>
          <FormattedMessage {...messages.title} />
        </DashboardModal.Header>

        <Box display="flex" flexDirection="column" gap={4}>
          <Text size={2} color="default2">
            <FormattedMessage {...messages.description} />
          </Text>

          <Box display="flex" flexDirection="column" gap={2}>
            <Text size={3} fontWeight="medium">
              <FormattedMessage {...messages.merchantIdLabel} />
            </Text>
            <input
              style={inputStyle}
              value={merchantId}
              onChange={e => setMerchantId(e.target.value)}
              placeholder="1234567890"
            />
          </Box>

          <Box display="flex" flexDirection="column" gap={2}>
            <Text size={3} fontWeight="medium">
              <FormattedMessage {...messages.serviceAccountLabel} />
            </Text>
            <textarea
              style={{ ...inputStyle, minHeight: 120, fontFamily: "monospace" }}
              value={serviceAccountKey}
              onChange={e => setServiceAccountKey(e.target.value)}
              placeholder='{ "type": "service_account", ... }'
              autoComplete="off"
              spellCheck={false}
            />
            <Text size={1} color="default2">
              <FormattedMessage {...messages.serviceAccountHelp} />
            </Text>
          </Box>

          {result && (
            <Box
              padding={4}
              borderRadius={3}
              backgroundColor={result.failed > 0 ? "warning1" : "success1"}
            >
              <Text size={2} fontWeight="medium" display="block">
                <FormattedMessage
                  {...messages.resultSuccess}
                  values={{ succeeded: result.succeeded, total: result.total }}
                />
              </Text>
              {result.failed > 0 && (
                <Box marginTop={2}>
                  <Text size={2} display="block">
                    <FormattedMessage
                      {...messages.resultFailed}
                      values={{ failed: result.failed }}
                    />
                  </Text>
                  {result.errorsSample.map((err, i) => (
                    <Text key={i} size={1} color="default2" display="block">
                      {err}
                    </Text>
                  ))}
                </Box>
              )}
              {result.feedUrl && (
                <Box marginTop={2}>
                  <Text size={1} color="default2" display="block">
                    <FormattedMessage {...messages.resultFeedUrl} />
                  </Text>
                  <Text size={1} display="block" style={{ wordBreak: "break-all" }}>
                    {result.feedUrl}
                  </Text>
                </Box>
              )}
            </Box>
          )}

          <Divider />
        </Box>

        <DashboardModal.Actions>
          <Button variant="secondary" color="text" onClick={handleClose}>
            <FormattedMessage {...buttonMessages.cancel} />
          </Button>
          <ConfirmButton
            transitionState={confirmState}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            <FormattedMessage {...messages.submit} />
          </ConfirmButton>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

export default GoogleExportModal;
